/**
 * CommentForm Component
 *
 * Story BL-024.2: Governance-Suite Comment Thread UI
 * Task 3: Create CommentForm component
 *
 * Thin wrapper around CommentComposer with maxLength=2000 for proposal comments.
 * Handles the cancel button for inline reply forms.
 *
 * @module components/governance/CommentForm
 */

import { CommentComposer } from './CommentComposer';

// =============================================================================
// Types
// =============================================================================

export interface CommentFormProps {
  /** Callback when comment content is submitted */
  onSubmit: (content: string) => Promise<void>;
  /** Callback to cancel (used for inline reply forms) */
  onCancel?: () => void;
  /** Placeholder text for the textarea */
  placeholder?: string;
  /** Whether this is a reply form (shows cancel button) */
  isReply?: boolean;
  /** Initial content value (used for edit mode pre-fill) */
  initialValue?: string;
}

// =============================================================================
// Component
// =============================================================================

/**
 * CommentForm wraps CommentComposer with proposal comment constraints.
 *
 * - maxLength is set to 2000 (per BL-024 spec)
 * - Cancel button shown for inline reply forms
 */
export function CommentForm({
  onSubmit,
  onCancel,
  placeholder,
  isReply,
  initialValue,
}: CommentFormProps) {
  return (
    <div data-testid={isReply ? 'reply-form' : 'comment-form'}>
      <CommentComposer
        maxLength={2000}
        onSubmit={onSubmit}
        placeholder={placeholder ?? 'Add a comment…'}
        initialValue={initialValue}
      />
      {isReply && onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-muted-foreground hover:underline mt-1"
          data-testid="cancel-reply-button"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

export default CommentForm;
